import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import {
  useCompleteLogin,
  useResendOtp,
  useResendOtpUnverified,
  useVerifyUser,
} from "../../tanstack-hooks";
import { ApiError, ErrorResponse } from "@/generated-api";
import {
  SignInFlowType,
  SUCCESSFUL_LOGIN_TYPE,
  SuccessfulLoginType,
} from "./SignInFlowTypes";
import { useNavigate } from "react-router-dom";
import { useLocaleTranslation } from "@/hooks/useLocaleTranslation";
import { useAuthStore } from "@/stores/authStore";
import { clearStoredUtms, getStoredUtms } from "@/lib/utms";

export const REGEXP_ONLY_DIGITS_AND_LETTERS = "^[a-zA-Z0-9]+$";
const OTP_RESEND_TIMEOUT_SECONDS = 60;

export default function LoginOtpCard({
  email,
  onGoBack,
  type,
  fingerprint,
}: {
  email: string;
  onGoBack: () => void;
  type: SignInFlowType;
  fingerprint: string;
}) {
  const { setToken } = useAuthStore();
  const [otp, setOTP] = useState("");
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(OTP_RESEND_TIMEOUT_SECONDS);
  const t = useLocaleTranslation();

  const navigate = useNavigate();
  const loginMutation = useCompleteLogin();
  const verifyUserMutation = useVerifyUser();
  const resendOtpVerified = useResendOtp();
  const resendOtpUnverified = useResendOtpUnverified();

  const completeLoginMutation =
    type === SignInFlowType.LOGIN ? loginMutation : verifyUserMutation;
  const resendOtpMutation =
    type === SignInFlowType.LOGIN ? resendOtpVerified : resendOtpUnverified;

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOTPSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeLoginMutation.mutate(
      {
        email: email.toLowerCase(),
        otp_code: otp,
        fingerprint,
        ...getStoredUtms(),
      },
      {
        onSuccess: (data) => {
          setToken(data.access_token);
          setSendOtpError(null);
          localStorage.setItem(
            SUCCESSFUL_LOGIN_TYPE,
            SuccessfulLoginType.EMAIL,
          );
          clearStoredUtms();
          navigate("/");
        },
        onError: (error) => {
          const err = error as ApiError;
          if (err.status === 400) {
            const b = err.body as ErrorResponse;
            setSendOtpError(b.detail);
          } else {
            setSendOtpError(t.SHARED.ERRORS.TRY_AGAIN);
          }
        },
      },
    );
  };

  const handleResendCode = () => {
    if (countdown === 0) {
      resendOtpMutation.mutate(
        { email: email.toLowerCase() },
        {
          onSuccess: () => {
            setCountdown(OTP_RESEND_TIMEOUT_SECONDS);
            setSendOtpError(null);
          },
          onError: (error) => {
            const err = error as ApiError;
            if (err.status === 400) {
              const b = err.body as ErrorResponse;
              setSendOtpError(b.detail);
            } else {
              setSendOtpError(t.SHARED.ERRORS.TRY_AGAIN);
            }
          },
        },
      );
    }
  };

  const handleGoBack = () => {
    onGoBack();
    setOTP("");
    setSendOtpError(null);
  };

  const handleOTPChange = (value: string) => {
    setOTP(value.toUpperCase());
  };

  return (
    <Card className="dark px-2 rounded-3xl">
      <CardHeader className="space-y-4">
        <Button variant="ghost" className="w-20 px-0" onClick={handleGoBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.SHARED.LABELS.BACK_BUTTON}
        </Button>
        <CardTitle>{t.LOGIN_OTP_CARD.LABELS.LOGIN_CODE_TITLE}</CardTitle>
        <CardDescription>{t.LOGIN_OTP_CARD.LABELS.CHECK_EMAIL}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleOTPSubmit}>
          <div className="space-y-4">
            <div>
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={handleOTPChange}
                inputMode="text"
                pattern={REGEXP_ONLY_DIGITS_AND_LETTERS}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={completeLoginMutation.isPending}
            >
              {completeLoginMutation.isPending
                ? t.LOGIN_OTP_CARD.LABELS.SIGNING_IN
                : t.LOGIN_OTP_CARD.LABELS.SIGN_IN}
            </Button>
            {sendOtpError && (
              <div className="text-sm font-medium text-destructive">
                {sendOtpError}
              </div>
            )}
          </div>
        </form>
      </CardContent>
      <CardFooter>
        <Button
          variant="outline"
          onClick={handleResendCode}
          disabled={countdown > 0 || resendOtpMutation.isPending}
          className="w-full mt-4"
        >
          {resendOtpMutation.isPending
            ? t.LOGIN_OTP_CARD.LABELS.RESENDING
            : countdown > 0
              ? `${t.LOGIN_OTP_CARD.LABELS.RESEND_CODE} (${countdown}s)`
              : t.LOGIN_OTP_CARD.LABELS.RESEND_CODE}
        </Button>
      </CardFooter>
    </Card>
  );
}
